import { LitElement, html, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { i18n } from "../i18n";

export const DEFAULT_PAGE_SIZE = 10;

export function pageCount(totalItems: number, pageSize = DEFAULT_PAGE_SIZE) {
  return totalItems ? Math.ceil(totalItems / pageSize) : 0;
}

export function clampPage(
  page: number,
  totalItems: number,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  return Math.min(
    Math.max(1, page),
    Math.max(1, pageCount(totalItems, pageSize)),
  );
}

export function pageItems<T>(
  items: T[],
  page: number,
  pageSize = DEFAULT_PAGE_SIZE,
) {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
}

@customElement("app-pagination")
export class AppPagination extends LitElement {
  @property({ type: Number }) page = 1;
  @property({ type: Number, attribute: "total-pages" }) totalPages = 0;
  @property({ type: Boolean }) busy = false;
  @state() private requestedPage = "1";
  @state() private error = "";

  private readonly languageChanged = () => this.requestUpdate();

  protected createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    window.addEventListener("languagechange", this.languageChanged);
  }

  disconnectedCallback() {
    window.removeEventListener("languagechange", this.languageChanged);
    super.disconnectedCallback();
  }

  protected updated(changed: PropertyValues<this>) {
    if (changed.has("page")) {
      this.requestedPage = String(this.page);
      this.error = "";
    }
  }

  private changePage(page: number) {
    if (this.busy || page === this.page || page < 1 || page > this.totalPages) {
      return;
    }
    this.error = "";
    this.dispatchEvent(
      new CustomEvent<{ page: number }>("page-change", {
        detail: { page },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private jump() {
    const value = this.requestedPage.trim();
    if (!/^\d+$/.test(value)) {
      this.error = i18n.t("paginationInvalidPage", {
        total: this.totalPages,
      });
      return;
    }
    const page = Number(value);
    if (page < 1 || page > this.totalPages) {
      this.error = i18n.t("paginationInvalidPage", {
        total: this.totalPages,
      });
      return;
    }
    this.error = "";
    if (page === this.page) {
      this.requestedPage = String(this.page);
      return;
    }
    this.changePage(page);
  }

  render() {
    const hasPages = this.totalPages > 0;
    const displayPage = hasPages ? this.page : 0;
    return html`
      <nav class="pagination" aria-label=${i18n.t("paginationLabel")}>
        <a2k-button
          class="pagination-previous"
          type="button"
          ?disabled=${this.busy || !hasPages || this.page <= 1}
          @click=${() => this.changePage(this.page - 1)}
        >
          ${i18n.t("previous")}
        </a2k-button>
        <span class="pagination-jump">
          <span class="pagination-summary" aria-live="polite">
            ${i18n.t("paginationPageSummary", {
              page: displayPage,
              total: this.totalPages,
            })}
          </span>
          <input
            class="a2k-native-input page-jump-input"
            type="number"
            inputmode="numeric"
            min="1"
            max=${Math.max(1, this.totalPages)}
            step="1"
            .value=${this.requestedPage}
            ?disabled=${this.busy || !hasPages}
            aria-label=${i18n.t("paginationPageInput")}
            aria-invalid=${this.error ? "true" : "false"}
            @input=${(event: Event) => {
              this.requestedPage = (event.target as HTMLInputElement).value;
              this.error = "";
            }}
            @keydown=${(event: KeyboardEvent) => {
              if (event.key === "Enter") this.jump();
            }}
          />
          <a2k-button
            class="pagination-go"
            type="button"
            ?disabled=${this.busy || !hasPages}
            @click=${this.jump}
          >
            ${i18n.t("goToPage")}
          </a2k-button>
        </span>
        <a2k-button
          class="pagination-next"
          type="button"
          ?disabled=${this.busy || !hasPages || this.page >= this.totalPages}
          @click=${() => this.changePage(this.page + 1)}
        >
          ${i18n.t("next")}
        </a2k-button>
        ${
          this.error
            ? html`<span
                class="status status-error pagination-error"
                role="alert"
                >${this.error}</span
              >`
            : null
        }
      </nav>
    `;
  }
}
